import { useQuery } from '@apollo/client';
import { READ_RECEIPT_WITH_SALES_ORDER } from '@queries/readReceiptWithSalesOrder';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import {
  BaseText,
  BottomButtonsLayout,
  Button,
  Headline1,
  Meta,
  Spinner,
  theme,
} from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import AttachmentTableRow from 'components/longTermParking/AttachmentTableRow';
import WaitingNumber from 'components/longTermParking/waitingList/WaitingNumber';
import Sheet from 'components/sheets/Sheet';
import AccountContext from 'context/AccountContext';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import useBottomMenuHeight from 'hooks/useBottomMenuHeight';
import { usePatchOrderAttachmentLinks } from 'hooks/usePatchOrderAttachmentLinks';
import { useSalesOrderAttachmentScreen } from 'hooks/useSalesOrderAttachmentScreen';
import usePollSalesOrderStatus from 'hooks/usePollSalesOrderStatus';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { WebWrapper } from 'screens/account/SheetWrapper';
import PermitPaymentInfo from 'screens/permit/PermitPaymentInfo';
import SalesOrderPermitInfo from 'screens/permit/salesOrder/SalesOrderPermitInfo';
import { usePermitStore } from 'stores/permitStore';
import styled from 'styled-components/native';
import {
  ColumnWithMargins,
  ScreenContainer,
  StyledWrapper,
} from 'styles/ContainerStyles';
import {
  AdditionalDiscount,
  BillingFrequency,
  BillingType,
  CountryCode,
  Maybe,
  Pmc,
  Promotion,
  Query,
  QueryReadReceiptByTransactionIdArgs,
  SalesOrderStatus,
  UnifiedPermit,
} from 'types/generatedSchemaTypes';
import { RootNavProps, RootStackParamList } from 'types/navigation';
import { resolveAttachmentUi } from 'utils/attachmentUtils';
import { isWeb } from 'utils/deviceUtils';
import { showFeatureFlag } from 'utils/featureFlagUtils';
import {
  isPermitMissingVehicle,
  isTerminalOrderStatus,
  SalesOrderExpiration,
  salesOrderExpiration,
} from 'utils/permitUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import ActivateSalesOrderPermitButton from '../ActivateSalesOrderPermitButton';
import { ActivateInfoBox, PaymentErrorInfoBox } from '../PermitInfoBoxes';
import ResolvePaymentButton from '../ResolvePaymentButton';
import { SalesOrderReceipt } from '../SalesOrderReceiptClass';
import { WebContentWrapper } from '../styles';
import SalesOrderPermiPriceInfo from './SalesOrderPermitPriceInfo';
import { useUiStore } from 'stores/uiStore';

const GreyWrapper = styled.View`
  background-color: ${isWeb ? theme.colors.white : theme.colors.gray100};
`;

type ContentProps = {
  pmc: Maybe<Pmc> | undefined;
  totalPrice: number | undefined;
  useAdditionalDiscount: boolean;
  additionalDiscount: Maybe<AdditionalDiscount> | undefined;
  promotion: Maybe<Promotion> | undefined;
  country: Maybe<CountryCode> | undefined;
  billingFrequency: Maybe<BillingFrequency> | undefined;
  billingType: Maybe<BillingType> | undefined;
  salesOrderReceipt: SalesOrderReceipt;
  isHistory: boolean;
  isInvoicing: boolean;
  expiration: SalesOrderExpiration;
  orderStatus: Maybe<SalesOrderStatus> | undefined;
  serviceFee: number;
  isSubscription?: boolean;
  showAttachment?: boolean;
};

const Content = ({
  pmc,
  billingFrequency,
  billingType,
  country,
  salesOrderReceipt,
  totalPrice,
  useAdditionalDiscount,
  additionalDiscount,
  promotion,
  isHistory,
  isInvoicing,
  orderStatus,
  serviceFee,
  isSubscription = false,
  showAttachment = false,
}: ContentProps) => {
  return (
    <>
      {!isSubscription && (
        <>
          <StyledWrapper mb={0} maxWidth={400}>
            <BaseText center testID="permit-details-header">
              {pmc?.parkZone?.name}
              {pmc?.parkZone?.address?.city
                ? `, ${pmc?.parkZone.address.city}`
                : ''}
            </BaseText>
          </StyledWrapper>
          <StyledWrapper mb={5}>
            <BaseText center testID="permit-details-name">
              {pmc?.name}
            </BaseText>
          </StyledWrapper>
        </>
      )}
      <SalesOrderPermiPriceInfo
        pmc={pmc}
        totalPrice={totalPrice}
        useAdditionalDiscount={useAdditionalDiscount}
        additionalDiscount={additionalDiscount}
        promotion={promotion}
        billingFrequency={billingFrequency}
        billingType={billingType}
        validFrom={new Date(salesOrderReceipt.startDate ?? '')}
        country={country ?? 'SE'}
        serviceFee={serviceFee}
      />

      {orderStatus === 'WAITING_FOR_CSR_APPROVAL' && (
        <ColumnWithMargins mt={15} mb={15}>
          <WaitingNumber
            isSalesOrder
            waitingCount={undefined}
            orderStatus={orderStatus}
          />
        </ColumnWithMargins>
      )}

      <View>
        <PaymentErrorInfoBox salesOrderReceipt={salesOrderReceipt} />
        {salesOrderReceipt.isInProgress && (
          <ActivateInfoBox salesOrderReceipt={salesOrderReceipt} />
        )}
        <StyledWrapper mb={40}>
          <SalesOrderPermitInfo
            isHistory={isHistory}
            salesOrderReceipt={salesOrderReceipt}
            isSubscription={isSubscription}
          />
          {showAttachment && <AttachmentTableRow noBorder withTopBorder />}
        </StyledWrapper>
        {!isInvoicing && (
          <StyledWrapper mb={10}>
            <PermitPaymentInfo
              salesOrderReceipt={salesOrderReceipt}
              isHistory={isHistory}
              isSubscription={isSubscription}
            />
          </StyledWrapper>
        )}
      </View>
    </>
  );
};

const WebContent = ({ children }: { children: React.ReactNode }) => (
  <ScrollView>
    <StyledWrapper center>
      <WebContentWrapper>
        <ColumnWithMargins ml={20} mr={20}>
          {children}
        </ColumnWithMargins>
      </WebContentWrapper>
    </StyledWrapper>
  </ScrollView>
);

type ButtonProps = {
  isHistory: boolean;
  handleEditPermit: () => void;
  handleTerminatePermit: () => void;
  t: (text: string) => string;
  salesOrderReceipt: SalesOrderReceipt;
  isCancelable: boolean | undefined;
  isDisableAutomaticRenewal: boolean;
  permit: Maybe<UnifiedPermit>;
};

const Buttons: React.FC<ButtonProps> = ({
  isHistory,
  isCancelable,
  isDisableAutomaticRenewal,
  handleEditPermit,
  handleTerminatePermit,
  t,
  salesOrderReceipt,
  permit,
}) => {
  const {
    isUpdating,
    isActivating,
    isWaiting,
    isFailedRecurringPaymentForSalesOrder,
  } = salesOrderReceipt;

  const renderPermitActionButton = () => {
    if (isFailedRecurringPaymentForSalesOrder) {
      return (
        <StyledWrapper mb={0} mt={20}>
          <ResolvePaymentButton permit={permit as UnifiedPermit} />
        </StyledWrapper>
      );
    }

    if (
      isWaiting ||
      (isPermitMissingVehicle(permit?.registrationPlates) &&
        salesOrderReceipt.usageType !== 'SKIDATA' &&
        salesOrderReceipt.usageType !== 'CUSTOMER')
    ) {
      return (
        <ActivateSalesOrderPermitButton
          permit={permit as UnifiedPermit}
          originalSalesOrderReceipt={salesOrderReceipt}
        />
      );
    }

    return (
      <Button
        {...testIdentifiers('edit-permit-button')}
        testID="edit-permit-button"
        size="l"
        buttonType="tertiary"
        text={t('permit.editPermit')}
        onPress={handleEditPermit}
        disabled={isUpdating || isActivating}
      />
    );
  };

  return (
    <>
      {!isHistory && (
        <>
          <StyledWrapper mb={isCancelable ? 10 : 0}>
            {renderPermitActionButton()}
          </StyledWrapper>

          {isCancelable && (
            <Button
              {...testIdentifiers('cancel-permit-button')}
              testID="cancel-permit-button"
              size="l"
              buttonType="tertiary"
              text={
                isDisableAutomaticRenewal
                  ? t('permit.disableAutomaticRenewal')
                  : t('button.cancelPermit')
              }
              onPress={handleTerminatePermit}
            />
          )}
        </>
      )}
    </>
  );
};

type SalesOrderPermitContentProps = {
  salesOrderReceipt: SalesOrderReceipt;
  isHistory: boolean;
  permit: Maybe<UnifiedPermit>;
  isSubscription?: boolean;
};

const SalesOrderPermitContent = ({
  salesOrderReceipt,
  isHistory,
  permit,
  isSubscription = false,
}: SalesOrderPermitContentProps) => {
  const { t } = useTranslation();
  const { navigate, addListener } = useNavigation<RootNavProps>();
  const {
    countryCode,
    orderLines,
    totalAmount,
    isInvoicing,
    orderStatus,
    renewal,
    isWaitingActivationOrCSR,
    orderLineIdWithVersion,
  } = salesOrderReceipt || {};
  const {
    flags: { cancel_sales_order },
  } = useContext(FeatureFlagsContext);
  const showCancelButton = showFeatureFlag(cancel_sales_order);
  const isCancelable =
    !isInvoicing &&
    ((orderStatus === 'ACTIVE' && renewal) || isWaitingActivationOrCSR) &&
    showCancelButton;
  const isDisableAutomaticRenewal = !!isCancelable && !isWaitingActivationOrCSR;

  const orderLine = orderLines?.[0];
  const tabBarHeight = useBottomMenuHeight();

  const {
    pmc,
    billingFrequency,
    billingType,
    useAdditionalDiscount,
    additionalDiscount,
    promotion,
    serviceFeeAmount,
  } = orderLine ?? {};

  const historyCountry = isHistory
    ? salesOrderReceipt?.item?.countryCode
    : countryCode;

  const totalPrice = (totalAmount ?? 0) / 100;
  const serviceFeePrice = (serviceFeeAmount ?? 0) / 100;

  const expiration = salesOrderExpiration(salesOrderReceipt);

  const handleTerminatePermit = () => {
    usePermitStore.getState().setSelectedPermit(orderLineIdWithVersion);
    usePermitStore.getState().setConfirmDisableRenewalOnly(false);
    usePermitStore
      .getState()
      .setShowDisableRenewalDialog(isDisableAutomaticRenewal);
    useUiStore.getState().setSheetModal('terminatePermit');
  };

  useEffect(() => {
    const unsubscribe = addListener('beforeRemove', (e) => {
      if (useUiStore.getState().sheetModal !== 'terminatePermit') {
        return;
      }
      e.preventDefault();
      useUiStore.getState().setSheetModal('');
    });
    return unsubscribe;
  }, [addListener]);
  const handleEditPermit = () => {
    navigate('salesOrderPermitEdit', { orderLineIdWithVersion });
  };

  const buttonProps: ButtonProps = {
    isHistory,
    isCancelable,
    isDisableAutomaticRenewal,
    handleEditPermit,
    handleTerminatePermit,
    t,
    salesOrderReceipt,
    permit,
  };

  const hasAttachments = !!permit?.attachments?.length;
  const { showOnSalesOrder } = resolveAttachmentUi(pmc?.attachmentPolicy);
  const showAttachment =
    !isHistory &&
    !!salesOrderReceipt.uid &&
    (showOnSalesOrder || hasAttachments);

  const contentProps: ContentProps = {
    pmc,
    totalPrice,
    useAdditionalDiscount: !!useAdditionalDiscount,
    additionalDiscount,
    promotion,
    country: historyCountry,
    billingFrequency,
    billingType,
    salesOrderReceipt,
    isHistory,
    isInvoicing,
    expiration,
    orderStatus,
    serviceFee: serviceFeePrice,
    isSubscription,
    showAttachment,
  };

  if (isWeb) {
    return (
      <WebWrapper
        page={() => (
          <WebContent>
            <Content {...contentProps} />
            <Buttons {...buttonProps} />
          </WebContent>
        )}
        mt={0}
      />
    );
  }

  return (
    <ScreenContainer mb={1} mt={10} background={theme.colors.transparent}>
      <Sheet hideHandle snappingPoints={['100%']}>
        <ColumnWithMargins mt={20} ml={20} mr={20} mb={tabBarHeight}>
          <BottomButtonsLayout contentBottom={<Buttons {...buttonProps} />}>
            <Content {...contentProps} />
          </BottomButtonsLayout>
        </ColumnWithMargins>
      </Sheet>
    </ScreenContainer>
  );
};

const SalesOrderPermitDetails: React.FC<{ isHistory?: boolean }> = ({
  isHistory = false,
}) => {
  const { t } = useTranslation();
  const {
    params: { transactionId, orderLineIdWithVersion },
  } = useRoute<RouteProp<RootStackParamList, 'salesOrderPermitDetails'>>();

  const {
    customer: { permits },
  } = useContext(AccountContext);

  const { data, loading } = useQuery<
    Query,
    QueryReadReceiptByTransactionIdArgs
  >(READ_RECEIPT_WITH_SALES_ORDER, {
    skip: !transactionId,
    variables: {
      transactionId: transactionId as string,
    },
    errorPolicy: 'all',
  });

  const permit = permits?.find(
    (sOrder) => sOrder?.orderLineIdWithVersion === orderLineIdWithVersion
  ) as Maybe<UnifiedPermit>;

  const salesOrderReceipt = new SalesOrderReceipt(
    data?.readReceiptByTransactionId ?? permit
  );
  usePollSalesOrderStatus(
    salesOrderReceipt.uid,
    salesOrderReceipt.orderStatus ?? 'ACTIVE'
  );
  const orderLine = salesOrderReceipt?.orderLines?.[0];
  const isSubscription =
    salesOrderReceipt?.billingItems?.[0]?.type === 'AIMO_PLUS';
  const { billingFrequency, billingType } = orderLine || {};

  const orderUid = salesOrderReceipt.uid;
  const orderStatus = salesOrderReceipt.orderStatus;
  const orderAttachments = permit?.attachments;
  const pmc = orderLine?.pmc;
  const { showOnSalesOrder, requireOnSalesOrder } = resolveAttachmentUi(
    pmc?.attachmentPolicy
  );

  const onAttachmentChanged = usePatchOrderAttachmentLinks({
    onError: () => {
      showToast(t('error.updatePermit'), 'error');
    },
  });

  const isTerminalStatus = isTerminalOrderStatus(orderStatus);

  useSalesOrderAttachmentScreen({
    orderUid,
    orderAttachments,
    enableSalesOrderAttachments: showOnSalesOrder,
    additionalReadOnly: isHistory || isTerminalStatus || requireOnSalesOrder,
    onAttachmentChanged,
  });

  return (
    <>
      <GreyWrapper>
        <StyledWrapper
          center
          mb={5}
          ml={20}
          mr={20}
          {...testIdentifiers('permit-details-headline')}>
          <Headline1
            center
            {...testIdentifiers('permit-details-headline-text')}>
            {isSubscription
              ? t('account.aimoSubscription.title')
              : salesOrderReceipt.title}
          </Headline1>
        </StyledWrapper>
        <StyledWrapper center>
          {billingFrequency &&
            billingType !== 'ONE_TIME' &&
            !isSubscription && (
              <Meta
                testID="permit-details-headline-type"
                color={theme.colors.gray300}>
                {orderLine?.quantity ? `${orderLine.quantity} \u00D7 ` : ''}
                {t(`permit.type.${billingFrequency}`)}
              </Meta>
            )}
        </StyledWrapper>
      </GreyWrapper>
      {loading ? (
        <StyledWrapper center>
          <Spinner />
        </StyledWrapper>
      ) : (
        <SalesOrderPermitContent
          salesOrderReceipt={salesOrderReceipt}
          isHistory={isHistory}
          permit={permit}
          isSubscription={isSubscription}
        />
      )}
    </>
  );
};

export default SalesOrderPermitDetails;
